/* eslint-disable react-refresh/only-export-components -- provider + useTheme hook are intentionally co-located */
import { createContext, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { PALETTE } from '@/theme/palette';

type Theme = 'dark' | 'light';

interface ThemeContextType {
    theme: Theme;
    toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const getSystemTheme = (): Theme =>
    window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

const getInitialTheme = (): Theme => {
    const stored = localStorage.getItem('theme');
    if (stored === 'dark' || stored === 'light') {
        return stored;
    }
    return getSystemTheme();
};

export const ThemeProvider = ({ children }: { children: ReactNode }): JSX.Element => {
    const [theme, setTheme] = useState<Theme>(getInitialTheme);
    // Follow the OS preference until the user makes an explicit choice (persisted in localStorage).
    const [isManual, setIsManual] = useState<boolean>(() => localStorage.getItem('theme') !== null);

    // Reflect the current theme on <html> (Tailwind `darkMode: 'class'`) and in the browser chrome.
    // One effect owns both so they can never disagree. The theme-color meta is seeded by the
    // bootstrap script in index.html; from hydration onward this is its owner. Matching it to the
    // page ground is what makes Safari's tab bar / iOS toolbars blend into the page.
    useEffect(() => {
        const root = window.document.documentElement;
        root.classList.remove('light', 'dark');
        root.classList.add(theme);

        document
            .querySelector('meta[name="theme-color"]')
            ?.setAttribute('content', theme === 'dark' ? PALETTE.groundDark : PALETTE.groundLight);
    }, [theme]);

    // Live-track the OS preference only while in automatic (non-manual) mode.
    useEffect(() => {
        if (isManual) {
            return;
        }
        const media = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = (event: MediaQueryListEvent): void => {
            setTheme(event.matches ? 'dark' : 'light');
        };
        media.addEventListener('change', handleChange);
        return () => media.removeEventListener('change', handleChange);
    }, [isManual]);

    const toggleTheme = (): void => {
        setIsManual(true);
        setTheme((prev) => {
            const next: Theme = prev === 'dark' ? 'light' : 'dark';
            localStorage.setItem('theme', next);
            return next;
        });
    };

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useTheme = (): ThemeContextType => {
    const context = useContext(ThemeContext);
    if (context === undefined) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};
